import { Feature, FeatureCollection, Point } from 'geojson';
import mapboxgl, { LngLatLike } from 'mapbox-gl';
import { getSearchResults, getSearchResultsWithoutDisabledProjects, getUrlData } from '../component/project-cards';

const popupSettings = {
    classNames: {
        office: 'map-popup map-popup__office',
        'contact-card': 'map-popup map-popup__contact-card',
        project: 'map-popup map-popup__project',
    },
    maxWidths: {
        office: '100%',
        'contact-card': '100%',
        project: '560px',
    },
    offsets: {
        top: [0, 30] as [number, number],
        'top-left': [0, 30] as [number, number],
        'top-right': [0, 30] as [number, number],
        bottom: [0, -30] as [number, number],
        'bottom-left': [0, -30] as [number, number],
        'bottom-right': [0, -30] as [number, number],
        left: [30, 0] as [number, number],
        right: [-30, 0] as [number, number],
    },
};

interface ImageObject {
    src: string;
    name: string;
}

const addNavigation = (map: mapboxgl.Map): void => {
    map.addControl(new mapboxgl.FullscreenControl(), 'bottom-right');
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
};

const addImages = (map: mapboxgl.Map, images: ImageObject[]): void => {
    images.forEach((imageData: { src: string; name: string }) => {
        const { src, name } = imageData;
        map.loadImage(src, function (error, image) {
            if (error) throw error;
            map.addImage(name, image);
        });
    });
};

const setAllMarkersUnactive = (map: mapboxgl.Map, layerName: string): void => {
    map.setLayoutProperty(layerName, 'icon-image', 'unactive-marker');
};

const removePopup = (removeAll = false) => {
    const popup = document.getElementsByClassName('mapboxgl-popup');
    if (!removeAll && popup.length === 1) {
        return;
    } 
    else {
        for (let i = 0; i < popup.length; i++) {
            popup[i].remove()
          }
    }
};

const getBoundsFromFeatures = (features: Feature[]): mapboxgl.LngLatBounds => {
    let bounds = new mapboxgl.LngLatBounds();
    features.forEach(function (feature) {
        const coords = (feature.geometry as Point).coordinates as LngLatLike;
        bounds.extend(coords);
    });

    return bounds;
};

const filterData = ({ value, places, map, layerName }) => {
    let newGeoData = { ...places };
    const filteredFeatures = newGeoData.features.filter((feature: Feature) => feature.properties.title.includes(value));
    newGeoData = { ...newGeoData, features: filteredFeatures };
    map.getSource(layerName).setData(newGeoData);
    removePopup();
    setAllMarkersUnactive(map, layerName);
};

const getPlaceById = (features: Feature[], placeId: number): Feature => features.find((feature) => feature.id === placeId);

const createPopupHtml = (properties) => {
    const { type } = properties;

    if (type === 'project') {
        const { imageSrcUrl, dateAndPlace, title, description, contentUrl, pageIsDisabled } = properties;
        return `
            <div class="project-popup__image-wrapper">
                <img src="${imageSrcUrl}?width=150&amp;height=150&amp;quality=85" alt=""/>
            </div>
            <a class="project-popup${(!pageIsDisabled ? "" : " without-link-effect")}" href="${(!pageIsDisabled ? contentUrl : "#")}">
                <h3 class="project-popup__title">${title}</h3>
                <p class="project-popup__date-and-place">${dateAndPlace}</p>
                <p class="project-popup__description">${description}</p>
                <span class="project-popup__link"${(pageIsDisabled ? " style=\"visibility: hidden\"" : "")}></span>   
            </a>
        `;
    } else if (type === 'contact-card' || type === 'office') {
        const { address, emailAnchor, googleMapsLink, googleMapsLinkText, homePageAnchor, name, phoneNumbersAnchors, visitingAddressText, postalAddressText, visitingAddress, postalAddress, postalAddressAsSameAsVisitingAddress } = properties;
         const showPostalAddress = !postalAddressAsSameAsVisitingAddress && postalAddress.line1 && postalAddress.line2;

        return `
            <div class="contact-popup">
                <div class="container">
                    <div class="contact-popup__content-left">
                    ${homePageAnchor ?
                        `<a class="contact-popup__link" href="${homePageAnchor} "target="_blank">
                            <h3 class="contact-popup__heading">${name}</h3>
                            ${renderGoToLinkIcon()}
                        </a>` : `<h3 class="contact-popup__heading">${name}</h3>`
                    }
                    </div>
                    <div class="contact-popup__content-middle">
                        <div class="company__address visiting__address">
                        ${address ? ` <p>${address}</p>` : ''}
                        ${(showPostalAddress && visitingAddress.line1 && visitingAddress.line2) ? `<p><b>${visitingAddressText}</b></p>` : ''}
                        ${(visitingAddress.line1 && visitingAddress.line2) ? `<p>${visitingAddress.line1}</p>
                        <p>${visitingAddress.line2}</p>` : ''}
                    </div>
                    ${showPostalAddress ?
                        `<div class="company__address postal__address">
                            <p><b>${postalAddressText}</b></p>
                            <p>${postalAddress.line1}</p>
                            <p>${postalAddress.line2}</p>
                        </div>` : ''}      
                    </div>
                    <div class="contact-popup__content-right">
                        <div>
                            <p class="contact-popup__phone-numbers">${phoneNumbersAnchors}</p>
                            <p class="contact-popup__mail-wrapper">${emailAnchor}</p>
                        </div>
                        <a class="contact-popup__maps-link" href="${googleMapsLink}" target="_blank" rel="noopener noreferrer">
                            ${googleMapsLinkText}
                            ${renderGoToLinkIcon()}
                        </a>
                    </div>
                <div>
            <div>
        `;
    }
};

const renderGoToLinkIcon = () => `
    <svg class="vd-icon vd-icon-arrow-up-right" width="1em" height="1em" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true" fill="currentColor">
        <polygon points="38.22 4.04 18.17 4.03 18.17 8.03 36.56 8.04 36.87 8.35 11.43 33.79 14.25 36.62 39.7 11.17 40.01 11.48 40 29.89 44 29.89 44.01 9.83 38.22 4.04"></polygon>
    </svg>
`;

const setSelectedElement = ({
    map,
    feature,
    layerName,
    changeIcons = true,
}: {
    map: mapboxgl.Map;
    feature: Feature;
    layerName: string;
    changeIcons?: Boolean;
}): void | any => {
    const { geometry, properties, id } = feature;
    const coordinates = (geometry as Point).coordinates.slice() as LngLatLike;
    const { classNames, maxWidths, offsets } = popupSettings;
    const popupHtml = createPopupHtml(properties);
    
    removePopup(true);
    const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: classNames[properties.type],
        maxWidth: maxWidths[properties.type],
        offset: offsets,
        focusAfterOpen: false,
    })
        .setLngLat(coordinates)
        .setHTML(popupHtml)
        .addTo(map);

    changeIcons &&
        map.setLayoutProperty(layerName, 'icon-image', [
            'match',
            ['id'], // get the feature id
            id,
            'active-marker', //image when id is the clicked feature id
            'unactive-marker', // default
        ]);
    return { popup };
};

const addAttribution = () => {
    const attributionHtml = `
        <button class="mapboxgl-ctrl-attrib-button" title="Toggle attribution" aria-label="Toggle attribution"></button>
        <div class="mapboxgl-ctrl-attrib-inner" role="list">
            <a href="https://www.mapbox.com/about/maps/" target="_blank" title="Mapbox" aria-label="Mapbox" role="listitem">
                © Mapbox
            </a>
            <a href="https://www.openstreetmap.org/about/" target="_blank" title="OpenStreetMap" aria-label="OpenStreetMap" role="listitem">
                © OpenStreetMap
            </a>
            <a
                href="https://apps.mapbox.com/feedback/?owner=wojtek-noa&amp;id=ckzfqrjwa006p15lposgug5tz&amp;access_token=pk.eyJ1Ijoid29qdGVrLW5vYSIsImEiOiJja3o2dHIxaWwwc3ZjMnhwa3lzZHF6ZDNjIn0.IGZ8B2VPTB1vnL66p6RqPA"
                target="_blank"
                title="Map feedback"
                aria-label="Map feedback"
                role="listitem"
                rel="noopener nofollow"
            >
                Improve this map
            </a>
        </div>
    `;
    const attributionEl = document.createElement('div');
    attributionEl.classList.add('mapboxgl-ctrl');
    attributionEl.classList.add('mapboxgl-ctrl-attrib');
    attributionEl.innerHTML = attributionHtml;

    const mapWrapperElements = document.querySelectorAll('.map-wrapper');
    if (mapWrapperElements.length) {
        mapWrapperElements.forEach((element) => {
            element.appendChild(attributionEl);
        });
    }
};

const updateMapWithSearchResults = async ({
    map,
    layerName,
    places,
    guuid,
    langId,
    catIds,
    searchTerm,
}: {
    map: mapboxgl.Map;
    layerName: string;
    places: FeatureCollection;
    guuid: string;
    langId: number;
    catIds: number[];
    searchTerm: string;
}) => {
    const results = await getSearchResults({ guuid, langId, catIds, searchTerm });
    const resultIds = results.pageSearchHits.map((item) => item.contentLinkId);

    const mapSource = map.getSource(layerName) as any;
    let newGeoData = { ...places };
    const filteredFeatures = newGeoData.features.filter((feature: Feature) => resultIds.includes(Number(feature.id)));
    newGeoData = newGeoData = { ...newGeoData, features: filteredFeatures };

    mapSource.setData(newGeoData);
    removePopup();
    setAllMarkersUnactive(map, layerName);
};

const addFilter = async ({
    map,
    layerName,
    places,
    spiderifier,
}: {
    map: mapboxgl.Map;
    layerName: string;
    places: FeatureCollection;
    spiderifier: any;
}) => {
    const filterBoxEl = document.querySelector('.filter-box');
    if (!filterBoxEl) return;

    const { lang, guuid } = (filterBoxEl as HTMLElement).dataset;
    const langId = Number(lang);

    const filterSubmitButtonEl = filterBoxEl.querySelector('.filter-button--submit');
    const filterAbortButtonEl = filterBoxEl.querySelector('.filter-button--abort');

    filterSubmitButtonEl.addEventListener('click', function () {
        const { catIds, searchTerm } = getUrlData();

        spiderifier.unspiderfy();
        updateMapWithSearchResults({
            map,
            layerName,
            places,
            guuid,
            langId,
            catIds,
            searchTerm,
        });
    });
    filterAbortButtonEl.addEventListener('click', function () {
        const { catIds, searchTerm } = getUrlData();

        spiderifier.unspiderfy();
        updateMapWithSearchResults({
            map,
            layerName,
            places,
            guuid,
            langId,
            catIds,
            searchTerm,
        });
    });
};

const addSearch = async ({
    map,
    layerName,
    places,
    spiderifier,
}: {
    map: mapboxgl.Map;
    layerName: string;
    places: FeatureCollection;
    spiderifier: any;
}) => {
    const inputWrapperEl = document.querySelector('.input-search-wrapper') as HTMLElement;
    const inputEl = inputWrapperEl.querySelector('.input-cards-filter') as HTMLInputElement;
    const iconEl = inputWrapperEl.querySelector('.search-icon') as HTMLInputElement;

    const { lang, guuid } = (inputWrapperEl as HTMLElement).dataset;
    const { catIds, searchTerm } = getUrlData();
    const langId = Number(lang);

    updateMapWithSearchResults({
        map,
        layerName,
        places,
        guuid,
        langId,
        catIds,
        searchTerm,
    });

    inputEl.addEventListener('keyup', async function (e) {
        const event = e as KeyboardEvent;
        if (event.key === 'Enter') {
            spiderifier.unspiderfy();
            updateMapWithSearchResults({
                map,
                layerName,
                places,
                guuid,
                langId,
                catIds,
                searchTerm: e.target['value'],
            });
        }
    });
    iconEl.addEventListener('click', function () {
        spiderifier.unspiderfy();
        updateMapWithSearchResults({
            map,
            layerName,
            places,
            guuid,
            langId,
            catIds,
            searchTerm: inputEl.value,
        });
    });
};

export {
    addNavigation,
    addImages,
    setAllMarkersUnactive,
    removePopup,
    getBoundsFromFeatures,
    filterData,
    getPlaceById,
    setSelectedElement,
    addAttribution,
    addSearch,
    addFilter,
};
